<template>
  <div class="qr-list">
    <page-header
      :title="'QR - ' + toLatin(title)"
      :subtitle="subtitle || $t('qr_codes_list')"
      icon="mdi-qrcode-scan"
      icon-color="primary"
    >
      <template #actions>
        <v-btn color="primary" depressed @click="printSinglePage" :loading="loadingPrint">
          <v-icon>mdi-printer</v-icon>
          {{ $t("print") }}
        </v-btn>
      </template>
    </page-header>

    <!-- Desktop: Print settings and filters -->
    <company-employees-qr-table
      ref="qrTable"
      :employees="currentItems"
      :restaurantName="title"
      :show-print-settings="showPrintSettings"
      :hide-print-button="true"
      @cardUpdated="card = $event"
    >
      <slot name="toolbar-filters"></slot>
      <template #additional-filters>
        <slot name="additional-filters"></slot>
      </template>
    </company-employees-qr-table>

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          id="qrTable"
          :headers="computedHeaders"
          :items="tableItems"
          class="ordera-box-shadow striped-table"
          mobile-breakpoint="0"
          :loading="loading"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          @current-items="currentItems = $event"
          @page-count="pageCount = $event"
        >
          <!-- QR Code Column -->
          <template v-slot:item.qrCode="{ item, index }">
            <div class="d-flex justify-center pt-2 pb-1">
              <div
                class="qr-code-container"
                @click="singleQrPrint(item, index)"
                :title="$t('click_to_print')"
              >
                <qrcode-vue
                  :id="`qrkodstampa${item.id}`"
                  :value="item.qrCode"
                  :size="120"
                  level="H"
                  render-as="svg"
                  class="qr-code"
                />
              </div>
            </div>
          </template>

          <template v-slot:item.category="{ item }">
            {{ item && item.category ? toLatin(item.category.name) : "" }}
          </template>
          <!-- Image Column (for foods) -->
          <template v-slot:item.image="{ item }">
            <div class="d-flex align-center justify-center py-1">
              <v-icon v-if="!item.image" style="font-size: 40px" color="grey lighten-1">
                mdi-image-off
              </v-icon>
              <v-img
                v-else-if="item.image && item.image.name === 'no-image.png'"
                :src="item.image.url"
                height="40"
                width="40"
                contain
              />
              <v-img
                v-else-if="item.image && conf && restaurantId"
                :src="`${conf.apiUrlBase}/gallery/${restaurantId}/medium/${item.image.name}`"
                height="40"
                width="40"
                contain
              />
            </div>
          </template>

          <template v-slot:item.name="{ item }">
            {{ toLatin(item.name) }}
          </template>

          <template v-slot:item.lastname="{ item }">
            {{ toLatin(item.lastname) }}
          </template>

          <!-- Type Column (only for employees) -->
          <template v-slot:item.type="{ item }">
            <v-chip
              :color="getEmployeeTypeColor(item.type)"
              text-color="white"
              small
            >
              <v-icon left x-small>
                {{ getEmployeeTypeIcon(item.type) }}
              </v-icon>
              {{ getEmployeeTypeText(item.type) }}
            </v-chip>
          </template>
        </v-data-table>

        <v-row align="center" justify="end" no-gutters class="mt-4">
          <text-field
            :value="itemsPerPage"
            :label="itemsPerPageLabel"
            type="number"
            :min="1"
            @input="itemsPerPage = parseInt($event, 10)"
          ></text-field>
          <v-pagination class="ml-4" v-model="page" :length="pageCount"></v-pagination>
        </v-row>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <slot name="mobile-filters"></slot>
      <div class="px-3 mt-3">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="tableItems"
          :loading="loading"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="2"
          :bold-fields="['fullName']"
        >
          <template v-slot:item.fullName="{ item }">
            {{ toLatin([item.name, item.lastname].filter(Boolean).join(" ")) }}
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip
              :color="getEmployeeTypeColor(item.type)"
              text-color="white"
              x-small
            >
              {{ getEmployeeTypeText(item.type) }}
            </v-chip>
          </template>
          <template v-slot:item.qrCode="{ item }">
            <div class="d-flex justify-center py-2">
              <qrcode-vue
                :id="`qrkodstampa${item.id}`"
                :value="item.qrCode"
                :size="80"
                level="H"
                render-as="svg"
                class="qr-code"
              />
            </div>
          </template>
        </mobile-data-cards>
      </div>
    </template>
  </div>
</template>

<script>
import CompanyEmployeesQrTable from "@/components/company/employee/tables/CompanyEmployeesQrTable.vue";
import QrcodeVue from "qrcode.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasEmployeeTypes from "@/mixins/hasEmployeeTypes";

export default {
  name: "QrListTable",
  mixins: [hasEmployeeTypes],
  components: {
    CompanyEmployeesQrTable,
    QrcodeVue,
    PageHeader,
    MobileDataCards,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      required: true,
    },
    backRoute: {
      type: [Object, String],
      required: true,
    },
    backLabel: {
      type: String,
      required: true,
    },
    showPrintSettings: {
      type: Boolean,
      default: false,
    },
    itemsPerPageLabel: {
      type: String,
      default: function () {
        return this.$t("items_per_page");
      },
    },
    conf: {
      type: Object,
      default: null,
    },
    restaurantId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    currentItems: [],
    card: {
      search: "",
      width: "90",
      height: "50",
      marginTop: "25",
      fontSize: "13",
      type: 2,
      visible_fields: [1, 2, 3, 4],
    },
    loadingPrint: false,
  }),
  computed: {
    tableItems() {
      return this.customSearch(this.items, this.headers, this.card.search);
    },
    computedHeaders() {
      return this.headers;
    },
    mobileHeaders() {
      return [
        { text: this.$t("name"), value: "fullName", cols: 6 },
        { text: this.$t("role"), value: "type", cols: 6 },
        { text: this.$t("qr_code"), value: "qrCode", cols: 12 },
      ];
    },
  },
  methods: {
    getEmployeeTypeText(typeValue) {
      if (!typeValue) return "";
      const types = Object.values(this.employeeTypes);
      const found = types.find((t) => t.val === typeValue);
      return found ? found.text : typeValue;
    },
    getEmployeeTypeIcon(typeValue) {
      if (typeValue === "customer") return "mdi-account";
      if (typeValue === "company") return "mdi-domain";
      if (typeValue === "table") return "mdi-table-furniture";
      if (typeValue === "dostava") return "mdi-moped";
      return "mdi-account-tie"; // Default ikonica
    },
    getEmployeeTypeColor(typeValue) {
      if (typeValue === "customer") return "primary";
      if (typeValue === "company") return "secondary";
      if (typeValue === "dostava") return "warning";
      return "info"; // Default boja
    },
    singleQrPrint(item, index) {
      if (this.$refs.qrTable) {
        this.$refs.qrTable.singleQrPrint(item, index);
      }
    },
    printSinglePage() {
      if (this.$refs.qrTable) {
        this.$refs.qrTable.print();
      }
    },
  },
};
</script>
